import { ApiResponse, post } from "../helpers";
import {
  GridDataRequest,
  PagingInfo,
  PagingInfoWithAnyType,
} from "../models/commonModel";
import {
  CommentReviewReportFilterModel,
  CommentReviewReportModel,
  RegistrationStatusReportModel,
  RegistrationSummaryReportFilterModel,
  RegistrationSummaryReportModel,
  StudyComplianceReportModel,
  StudyConsentReportFilterModel,
  StudyConsentReportModel,
  StudyConsentTypeFilterResponse,
  WithdrawalReportFilterModel,
  WithdrawalReportRowModel,
  StudyRegistrationDropOffReportFilterModel,
  StudyRegistrationDropOffReportModel,
  StudyScheduleReportModel,
  UserTypeFilterResponse
} from "../models/reportModel";
import { ReportEndpoints } from "./configEndPoints";

const getCommentReviewReport = async (
  pagingInfo: PagingInfo,
  anyParms: CommentReviewReportFilterModel[]
): Promise<ApiResponse<CommentReviewReportModel[]>> => {
  return post<PagingInfoWithAnyType, CommentReviewReportModel[]>(
    ReportEndpoints.GetCommentReviewReport,
    { ...pagingInfo, anyParms }
  );
};

const getRegistrationStatusReport = async (
  pagingInfo: PagingInfo,
  anyParm: CommentReviewReportFilterModel
): Promise<ApiResponse<RegistrationStatusReportModel[]>> => {
  return post<GridDataRequest, RegistrationStatusReportModel[]>(
    ReportEndpoints.GetRegistrationStatusReport,
    { ...pagingInfo, anyParm }
  );
};

const getRegistrationSummaryReport = async (
  pagingInfo: PagingInfo,
  anyParm: RegistrationSummaryReportFilterModel
): Promise<ApiResponse<RegistrationSummaryReportModel[]>> => {
  return post<GridDataRequest, RegistrationSummaryReportModel[]>(
    ReportEndpoints.GetRegistrationSummaryReport,
    { ...pagingInfo, anyParm }
  );
};

const downloadRegistrationStatusReport = async (
  startDate: Date | null,
  endDate: Date | null,
  hierarchyId: number,
  filtersObj: any,
  downloadType: string
): Promise<ApiResponse<any>> => {
  return post<any, any>(ReportEndpoints.DownloadRegistrationStatusReport, {
    startDate,
    endDate,
    hierarchyId,
    ...filtersObj,
    downloadType,
  });
};

const getStudyScheduleReport = async (
  pagingInfo: PagingInfo,
  anyParm: CommentReviewReportFilterModel
): Promise<ApiResponse<StudyScheduleReportModel[]>> => {
  return post<GridDataRequest, StudyScheduleReportModel[]>(
    ReportEndpoints.GetStudyScheduleReport,
    { ...pagingInfo, anyParm }
  );
};

const getStudyScheduleReportColumns = async (
  anyParm: CommentReviewReportFilterModel
): Promise<ApiResponse<string[]>> => {
  return post<any, string[]>(
    ReportEndpoints.GetStudyScheduleReportColumns,
    anyParm
  );
};

const downloadStudyScheduleReport = async (
  startDate: Date | null,
  endDate: Date | null,
  hierarchyId: number,
  filtersObj: any,
  downloadType: string
): Promise<ApiResponse<any>> => {
  return post<any, any>(ReportEndpoints.DownloadStudyScheduleReport, {
    startDate,
    endDate,
    hierarchyId,
    ...filtersObj,
    downloadType,
  });
};

const downloadRegistrationSummaryReport = async (
  startDate: Date | null,
  endDate: Date | null,
  hierarchyId: number,
  filtersObj: any,
  downloadType: string
): Promise<ApiResponse<any>> => {
  return post<any, any>(ReportEndpoints.DownloadRegistrationSummaryReport, {
    startDate,
    endDate,
    hierarchyId,
    ...filtersObj,
    downloadType,
  });
};

const getUserTypesForRegistrationSummary = async (): Promise<ApiResponse<UserTypeFilterResponse>> => {
  return post<any, UserTypeFilterResponse>(
    ReportEndpoints.GetUserTypesForRegistrationSummary,
    null // or undefined
  );
};

const getStudyConsentReport = async (
    pagingInfo: PagingInfo,
    anyParm: StudyConsentReportFilterModel
): Promise<ApiResponse<StudyConsentReportModel[]>> => {
    return post<GridDataRequest, StudyConsentReportModel[]>(
        ReportEndpoints.GetStudyConsentReport,
        { ...pagingInfo, anyParm }
    );
};

const getWithdrawalReport = async (
  pagingInfo: PagingInfo,
  anyParm: WithdrawalReportFilterModel
): Promise<ApiResponse<WithdrawalReportRowModel[]>> => {
  return post<GridDataRequest, WithdrawalReportRowModel[]>(
    ReportEndpoints.GetWithdrawalReport,
    { ...pagingInfo, anyParm }
  );
};

const getConsentTypesForStudyConsentReport = async (
    anyParm: string | null
): Promise<ApiResponse<StudyConsentTypeFilterResponse>> => {
    return post<any, StudyConsentTypeFilterResponse>(
        ReportEndpoints.GetConsentTypesForStudyConsentReport,
        anyParm
    );
};

const downloadStudyConsentReport = async (
    startDate: Date | null,
    endDate: Date | null,
    hierarchyId: number,
    filtersObj: any,
    downloadType: string
): Promise<ApiResponse<any>> => {
    return post<any, any>(ReportEndpoints.DownloadStudyConsentReport, {
        startDate,
        endDate,
        hierarchyId,
        ...filtersObj,
        downloadType,
    });
};

const getStudyComplianceReport = async (
  pagingInfo: PagingInfo,
  anyParm: CommentReviewReportFilterModel
): Promise<ApiResponse<StudyComplianceReportModel[]>> => {
  return post<GridDataRequest, StudyComplianceReportModel[]>(
    ReportEndpoints.GetStudyComplianceReport,
    { ...pagingInfo, anyParm }
  );
};

const downloadStudyComplianceReport = async (
  startDate: Date | null,
  endDate: Date | null,
  hierarchyId: number,
  filtersObj: any,
  downloadType: string
): Promise<ApiResponse<any>> => {
  return post<any, any>(ReportEndpoints.DownloadStudyComplianceReport, {
    startDate,
    endDate,
    hierarchyId,
    ...filtersObj,
    downloadType,
  });
};

const getStudyRegistrationDropOffReport = async (
  filterModel: StudyRegistrationDropOffReportFilterModel
): Promise<ApiResponse<StudyRegistrationDropOffReportModel>> => {
  return post<StudyRegistrationDropOffReportFilterModel, StudyRegistrationDropOffReportModel>(
    ReportEndpoints.GetStudyRegistrationDropOffReport,
    filterModel
  );
};

const downloadStudyRegistrationDropOffReport = async (
  startDate: Date | null,
  endDate: Date | null,
  hierarchyId: number,
  filtersObj: any,
  downloadType: string
): Promise<ApiResponse<any>> => {
  const requestModel = {
    startDate,
    endDate,
    downloadType
  };
  
  return post(
    ReportEndpoints.DownloadStudyRegistrationDropOffReport,
    requestModel
  );
};

// eslint-disable-next-line @typescript-eslint/naming-convention
const ReportApi = {
  getCommentReviewReport,
  getRegistrationStatusReport,
  downloadRegistrationStatusReport,
  getStudyScheduleReport,
  getStudyScheduleReportColumns,
  downloadStudyScheduleReport,
  getRegistrationSummaryReport,
  downloadRegistrationSummaryReport,
  getUserTypesForRegistrationSummary,
  getConsentTypesForStudyConsentReport,
  getStudyConsentReport,
  getWithdrawalReport,
  downloadStudyConsentReport,
  getStudyComplianceReport,
  downloadStudyComplianceReport,
  getStudyRegistrationDropOffReport,
  downloadStudyRegistrationDropOffReport,  
};

export default ReportApi;
